import React, { useEffect, useState, useContext, useCallback } from 'react';
import { request } from '../api';
import { listProfiles, createUser, updateProfile, deleteProfile } from '../utils/adminApi';
import ToastContext from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { FiEdit2, FiTrash2, FiLock, FiUnlock, FiSearch, FiPlus, FiRotateCcw } from 'react-icons/fi';
import { isDemoAdmin, DEMO_ADMIN_MESSAGE } from '../utils/adminDemoMode';

const AdminUsers = () => {
  const toast = useContext(ToastContext);
  const { currentUser } = useAuth();

  // Users state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [filterEmail, setFilterEmail] = useState('');
  const [filterFirstName, setFilterFirstName] = useState('');
  const [filterLastName, setFilterLastName] = useState('');
  const [filterRole, setFilterRole] = useState('');

  // Sorting & Pagination
  const [sortConfig, setSortConfig] = useState({ key: 'email', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // UI state
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newDateOfBirth, setNewDateOfBirth] = useState('');
  const [newGender, setNewGender] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setUsers(await listProfiles());
    } catch (error) {
      if (toast?.error) toast.error('Erreur lors du chargement des utilisateurs');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filter users
  const filteredUsers = users.filter((user) => {
    const emailMatch = (user.email || '').toLowerCase().includes(filterEmail.toLowerCase());
    const firstNameMatch = (user.firstName || '').toLowerCase().includes(filterFirstName.toLowerCase());
    const lastNameMatch = (user.lastName || '').toLowerCase().includes(filterLastName.toLowerCase());
    const roleMatch = filterRole === '' || (filterRole === 'admin' ? user.isAdmin : !user.isAdmin);
    return emailMatch && firstNameMatch && lastNameMatch && roleMatch;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const key = sortConfig.key;
    const aValue = a[key] || '';
    const bValue = b[key] || '';
    if (typeof aValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const paginatedUsers = sortedUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);
  const totalPages = Math.ceil(sortedUsers.length / usersPerPage);

  // Reset filters
  const handleResetFilters = () => {
    setFilterEmail('');
    setFilterFirstName('');
    setFilterLastName('');
    setFilterRole('');
    setCurrentPage(1);
  };

  // Handle sort
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Create user
  const handleCreateUser = async () => {
    if (isDemoAdmin(currentUser)) {
      toast?.info(DEMO_ADMIN_MESSAGE);
      return;
    }
    if (!newEmail || !newFirstName || !newLastName || !newPassword) {
      if (toast?.error) toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      await createUser({
        email: newEmail,
        password: newPassword,
        firstName: newFirstName,
        lastName: newLastName,
        dateOfBirth: newDateOfBirth,
        gender: newGender,
        country: newCountry,
        phone: newPhone,
        isAdmin: newIsAdmin
      });
      if (toast?.success) toast.success('Utilisateur créé avec succès');
      setModal(null);
      resetCreateForm();
      fetchUsers();
    } catch (error) {
      console.error('Erreur Supabase lors de la création:', error);
      const message = error?.message || 'Erreur lors de la création de l\'utilisateur';
      if (toast?.error) toast.error(message);
    }
  };

  const resetCreateForm = () => {
    setNewEmail('');
    setNewFirstName('');
    setNewLastName('');
    setNewPassword('');
    setNewDateOfBirth('');
    setNewGender('');
    setNewCountry('');
    setNewPhone('');
    setNewIsAdmin(false);
  };

  // Edit user
  const handleEditUser = (user) => {
    setEditing(user._id);
    setNewEmail(user.email);
    setNewFirstName(user.firstName);
    setNewLastName(user.lastName);
    setNewDateOfBirth(user.dateOfBirth || '');
    setNewGender(user.gender || '');
    setNewCountry(user.country || '');
    setNewPhone(user.phone || '');
  };

  const handleSaveEdit = async (userId) => {
    if (isDemoAdmin(currentUser)) {
      toast?.info(DEMO_ADMIN_MESSAGE);
      return;
    }
    if (!newEmail || !newFirstName || !newLastName) {
      if (toast?.error) toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    try {
      await updateProfile(userId, {
          email: newEmail,
          firstName: newFirstName,
          lastName: newLastName,
          dateOfBirth: newDateOfBirth,
          gender: newGender,
          country: newCountry,
          phone: newPhone,
      });
      if (toast?.success) toast.success('Utilisateur mis à jour avec succès');
      setEditing(null);
      fetchUsers();
    } catch (error) {
      if (toast?.error) toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCancelEdit = () => {
    setEditing(null);
    resetCreateForm();
  };

  // Delete user
  const handleDeleteUser = (user) => {
    if (isDemoAdmin(currentUser)) {
      toast?.info(DEMO_ADMIN_MESSAGE);
      return;
    }
    setModal({ type: 'delete', user });
  };

  const confirmDelete = async () => {
    try {
      await deleteProfile(modal.user._id);
      if (toast?.success) toast.success('Utilisateur supprimé avec succès');
      setModal(null);
      fetchUsers();
    } catch (error) {
      if (toast?.error) toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle admin
  const handleToggleAdmin = (user) => {
    if (isDemoAdmin(currentUser)) {
      toast?.info(DEMO_ADMIN_MESSAGE);
      return;
    }
    setModal({ type: 'toggleAdmin', user });
  };

  const confirmToggleAdmin = async () => {
    try {
      await updateProfile(modal.user._id, { isAdmin: !modal.user.isAdmin });
      if (toast?.success) toast.success(modal.user.isAdmin ? 'Droits admin supprimés' : 'Droits admin accordés');
      setModal(null);
      fetchUsers();
    } catch (error) {
      if (toast?.error) toast.error('Erreur lors de la modification');
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 1.5rem', textAlign: 'center', color: 'var(--muted)' }}>
        Chargement des utilisateurs...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'transparent' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 1.5rem', animation: 'slideUpFade 0.6s ease-out both' }}>
        
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)', fontWeight: 900, marginBottom: '0.5rem', background: 'linear-gradient(135deg, var(--accent), var(--accent-2))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.5px', margin: 0 }}>
              Gestion des Utilisateurs
            </h1>
            <p style={{ fontSize: '1.05rem', color: 'var(--muted)', margin: '0.5rem 0 0 0', fontWeight: 500 }}>
              Gérez les comptes et les rôles
            </p>
          </div>
          <button
            onClick={() => { setModal({ type: 'create' }); resetCreateForm(); }}
            style={{
              padding: '1rem 1.75rem',
              background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 16px rgba(56, 189, 248, 0.2)',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(56, 189, 248, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(56, 189, 248, 0.2)';
            }}
          >
            <FiPlus size={20} /> Créer un utilisateur
          </button>
        </div>

        {/* Filtres */}
        <div style={{
          padding: '1.75rem',
          borderRadius: '20px',
          background: 'rgba(56, 189, 248, 0.04)',
          border: '1.5px solid rgba(56, 189, 248, 0.12)',
          marginBottom: '2rem',
          animation: 'slideUpFade 0.6s ease-out 0.1s both'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FiSearch size={18} /> Filtres de recherche
            </h3>
            <button
              onClick={handleResetFilters}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(56, 189, 248, 0.1)',
                color: 'var(--accent)',
                border: '1.5px solid rgba(56, 189, 248, 0.3)',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(56, 189, 248, 0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(56, 189, 248, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <FiRotateCcw size={16} /> Réinitialiser
            </button>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
            {[
              { label: 'Email', value: filterEmail, onChange: (v) => { setFilterEmail(v); setCurrentPage(1); }, placeholder: '📧 Filtrer par email' },
              { label: 'Prénom', value: filterFirstName, onChange: (v) => { setFilterFirstName(v); setCurrentPage(1); }, placeholder: '👤 Filtrer par prénom' },
              { label: 'Nom', value: filterLastName, onChange: (v) => { setFilterLastName(v); setCurrentPage(1); }, placeholder: '👤 Filtrer par nom' }
            ].map((filter, i) => (
              <div key={i}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '0.5rem' }}>
                  {filter.label}
                </label>
                <input
                  type="text"
                  value={filter.value}
                  onChange={(e) => filter.onChange(e.target.value)}
                  placeholder={filter.placeholder}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(56, 189, 248, 0.2)',
                    background: 'rgba(56, 189, 248, 0.03)',
                    color: 'var(--text)',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--accent)';
                    e.target.style.background = 'rgba(56, 189, 248, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(56, 189, 248, 0.2)';
                    e.target.style.background = 'rgba(56, 189, 248, 0.03)';
                  }}
                />
              </div>
            ))}
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '0.5rem' }}>
                Rôle
              </label>
              <select
                value={filterRole}
                onChange={(e) => { setFilterRole(e.target.value); setCurrentPage(1); }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '12px',
                  border: '1.5px solid rgba(56, 189, 248, 0.2)',
                  background: 'rgba(56, 189, 248, 0.03)',
                  color: 'var(--text)',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <option value="">👥 Tous les rôles</option>
                <option value="admin">🔐 Admin</option>
                <option value="user">👤 Utilisateur</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div style={{
          borderRadius: '20px',
          border: '1.5px solid rgba(56, 189, 248, 0.12)',
          overflow: 'hidden',
          animation: 'slideUpFade 0.6s ease-out 0.2s both'
        }}>
          {paginatedUsers.length === 0 ? (
            <div style={{ padding: '3rem 1.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔍</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                Aucun utilisateur trouvé
              </h3>
              <p style={{ color: 'var(--muted)' }}>Ajustez vos filtres ou créez un nouvel utilisateur</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: 'rgba(56, 189, 248, 0.08)', borderBottom: '2px solid rgba(56, 189, 248, 0.15)' }}>
                    <tr>
                      {['Email', 'Prénom', 'Nom', 'Pays', 'Rôle', 'Actions'].map((header, i) => (
                        <th
                          key={i}
                          onClick={() => i < 4 && handleSort(['email', 'firstName', 'lastName', 'country'][i])}
                          style={{
                            padding: '1rem 1.25rem',
                            textAlign: 'left',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: 'var(--text)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            cursor: i < 4 ? 'pointer' : 'default',
                            userSelect: 'none',
                            transition: 'color 0.2s'
                          }}
                          onMouseEnter={(e) => { if (i < 4) e.currentTarget.style.color = 'var(--accent)'; }}
                          onMouseLeave={(e) => { if (i < 4) e.currentTarget.style.color = 'var(--text)'; }}
                        >
                          {header} {i < 4 && sortConfig.key === ['email', 'firstName', 'lastName', 'country'][i] && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user, idx) => (
                      <tr
                        key={user._id}
                        style={{
                          borderBottom: '1px solid rgba(56, 189, 248, 0.08)',
                          transition: 'all 0.2s ease',
                          background: idx % 2 === 0 ? 'rgba(56, 189, 248, 0.02)' : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(56, 189, 248, 0.08)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = idx % 2 === 0 ? 'rgba(56, 189, 248, 0.02)' : 'transparent';
                        }}
                      >
                        <td style={{ padding: '1rem 1.25rem', color: editing === user._id ? 'var(--accent)' : 'var(--text)', fontWeight: 500 }}>
                          {editing === user._id ? (
                            <input
                              type="email"
                              value={newEmail}
                              onChange={(e) => setNewEmail(e.target.value)}
                              style={{
                                padding: '0.5rem',
                                borderRadius: '8px',
                                border: '1.5px solid var(--accent)',
                                background: 'rgba(56, 189, 248, 0.05)',
                                color: 'var(--text)',
                                width: '100%',
                                fontSize: '0.9rem'
                              }}
                            />
                          ) : user.email}
                        </td>
                        <td style={{ padding: '1rem 1.25rem', color: 'var(--text)' }}>
                          {editing === user._id ? (
                            <input
                              type="text"
                              value={newFirstName}
                              onChange={(e) => setNewFirstName(e.target.value)}
                              style={{
                                padding: '0.5rem',
                                borderRadius: '8px',
                                border: '1.5px solid var(--accent)',
                                background: 'rgba(56, 189, 248, 0.05)',
                                color: 'var(--text)',
                                width: '100%',
                                fontSize: '0.9rem'
                              }}
                            />
                          ) : (user.firstName || '-')}
                        </td>
                        <td style={{ padding: '1rem 1.25rem', color: 'var(--text)' }}>
                          {editing === user._id ? (
                            <input
                              type="text"
                              value={newLastName}
                              onChange={(e) => setNewLastName(e.target.value)}
                              style={{
                                padding: '0.5rem',
                                borderRadius: '8px',
                                border: '1.5px solid var(--accent)',
                                background: 'rgba(56, 189, 248, 0.05)',
                                color: 'var(--text)',
                                width: '100%',
                                fontSize: '0.9rem'
                              }}
                            />
                          ) : (user.lastName || '-')}
                        </td>
                        <td style={{ padding: '1rem 1.25rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
                          {user.country || '-'}
                        </td>
                        <td style={{ padding: '1rem 1.25rem' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.5rem 1rem',
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            background: user.isAdmin ? 'rgba(168, 85, 247, 0.12)' : 'rgba(107, 114, 128, 0.12)',
                            color: user.isAdmin ? '#a855f7' : '#6b7280',
                            border: user.isAdmin ? '1.5px solid rgba(168, 85, 247, 0.3)' : '1.5px solid rgba(107, 114, 128, 0.3)'
                          }}>
                            {user.isAdmin ? '🔐 Admin' : '👤 Utilisateur'}
                          </span>
                        </td>
                        <td style={{ padding: '1rem 1.25rem' }}>
                          {editing === user._id ? (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => handleSaveEdit(user._id)}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: 'rgba(16, 185, 129, 0.12)',
                                  color: '#10b981',
                                  border: '1.5px solid rgba(16, 185, 129, 0.3)',
                                  borderRadius: '8px',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(16, 185, 129, 0.12)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                ✓ Sauv.
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: 'rgba(239, 68, 68, 0.12)',
                                  color: '#ef4444',
                                  border: '1.5px solid rgba(239, 68, 68, 0.3)',
                                  borderRadius: '8px',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                ✕ Annul.
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <ActionIconButton
                                icon={<FiEdit2 size={16} />}
                                onClick={() => handleEditUser(user)}
                                color="accent"
                                tooltip="Éditer"
                              />
                              <ActionIconButton
                                icon={user.isAdmin ? <FiLock size={16} /> : <FiUnlock size={16} />}
                                onClick={() => handleToggleAdmin(user)}
                                color="warning"
                                tooltip={user.isAdmin ? 'Retirer admin' : 'Faire admin'}
                              />
                              <ActionIconButton
                                icon={<FiTrash2 size={16} />}
                                onClick={() => handleDeleteUser(user)}
                                color="danger"
                                tooltip="Supprimer"
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ borderTop: '1px solid rgba(56, 189, 248, 0.12)', padding: '1.25rem', display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '0.5rem 0.85rem',
                        borderRadius: '8px',
                        border: currentPage === pageNum ? 'none' : '1.5px solid rgba(56, 189, 248, 0.2)',
                        background: currentPage === pageNum ? 'linear-gradient(135deg, var(--accent), var(--accent-2))' : 'transparent',
                        color: currentPage === pageNum ? 'white' : 'var(--text)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== pageNum) {
                          e.currentTarget.style.background = 'rgba(56, 189, 248, 0.08)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentPage !== pageNum) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Create/Edit Modal */}
        {modal?.type === 'create' && (
          <Modal onClose={() => { setModal(null); resetCreateForm(); }} title="Créer un nouvel utilisateur">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
              <FormField label="Email *" type="email" value={newEmail} onChange={setNewEmail} placeholder="email@example.com" />
              <FormField label="Prénom *" type="text" value={newFirstName} onChange={setNewFirstName} placeholder="Jean" />
              <FormField label="Nom *" type="text" value={newLastName} onChange={setNewLastName} placeholder="Dupont" />
              <FormField label="Mot de passe *" type="password" value={newPassword} onChange={setNewPassword} placeholder="••••••••" />
              <FormField label="Date de naissance" type="date" value={newDateOfBirth} onChange={setNewDateOfBirth} />
              <FormField label="Genre" type="select" value={newGender} onChange={setNewGender} options={[{ value: '', label: 'Non spécifié' }, { value: 'male', label: 'Homme' }, { value: 'female', label: 'Femme' }, { value: 'other', label: 'Autre' }]} />
              <FormField label="Pays" type="text" value={newCountry} onChange={setNewCountry} placeholder="France" />
              <FormField label="Téléphone" type="tel" value={newPhone} onChange={setNewPhone} placeholder="+33..." />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem', cursor: 'pointer', color: 'var(--text)', fontWeight: 600 }}>
              <input type="checkbox" checked={newIsAdmin} onChange={(e) => setNewIsAdmin(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
              🔐 Administrateur
            </label>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setModal(null); resetCreateForm(); }} style={{ padding: '0.75rem 1.5rem', background: 'rgba(56, 189, 248, 0.1)', color: 'var(--accent)', border: '1.5px solid rgba(56, 189, 248, 0.3)', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.1)'; }}>Annuler</button>
              <button onClick={handleCreateUser} style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, var(--accent), var(--accent-2))', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(56, 189, 248, 0.3)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>Créer</button>
            </div>
          </Modal>
        )}

        {/* Delete Confirmation Modal */}
        {modal?.type === 'delete' && (
          <Modal onClose={() => setModal(null)} title="Confirmer la suppression" isDanger={true}>
            <p style={{ color: 'var(--text)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{modal.user.email}</strong> ? Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} style={{ padding: '0.75rem 1.5rem', background: 'rgba(56, 189, 248, 0.1)', color: 'var(--accent)', border: '1.5px solid rgba(56, 189, 248, 0.3)', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.1)'; }}>Annuler</button>
              <button onClick={confirmDelete} style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.3)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>Supprimer</button>
            </div>
          </Modal>
        )}

        {/* Toggle Admin Confirmation Modal */}
        {modal?.type === 'toggleAdmin' && (
          <Modal onClose={() => setModal(null)} title="Modifier les droits admin">
            <p style={{ color: 'var(--text)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              {modal.user.isAdmin
                ? `Êtes-vous sûr de vouloir retirer les droits admin à ${modal.user.email} ?`
                : `Êtes-vous sûr de vouloir accorder les droits admin à ${modal.user.email} ?`}
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} style={{ padding: '0.75rem 1.5rem', background: 'rgba(56, 189, 248, 0.1)', color: 'var(--accent)', border: '1.5px solid rgba(56, 189, 248, 0.3)', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(56, 189, 248, 0.1)'; }}>Annuler</button>
              <button onClick={confirmToggleAdmin} style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, var(--accent), var(--accent-2))', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(56, 189, 248, 0.3)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>Confirmer</button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

// Modal Component
function Modal({ children, onClose, title, isDanger }) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        animation: 'fadeIn 0.3s ease-out'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: '20px',
          padding: '2rem',
          maxWidth: '600px',
          width: '90%',
          border: isDanger ? '1.5px solid rgba(239, 68, 68, 0.2)' : '1.5px solid rgba(56, 189, 248, 0.2)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          animation: 'slideUpFade 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', marginBottom: '1rem', marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {isDanger && '⚠️'} {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// FormField Component
function FormField({ label, type, value, onChange, placeholder, options }) {
  return (
    <div>
      <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '0.5rem' }}>
        {label}
      </label>
      {type === 'select' ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '12px',
            border: '1.5px solid rgba(56, 189, 248, 0.2)',
            background: 'rgba(56, 189, 248, 0.03)',
            color: 'var(--text)',
            fontSize: '0.95rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--accent)';
            e.target.style.background = 'rgba(56, 189, 248, 0.08)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(56, 189, 248, 0.2)';
            e.target.style.background = 'rgba(56, 189, 248, 0.03)';
          }}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '12px',
            border: '1.5px solid rgba(56, 189, 248, 0.2)',
            background: 'rgba(56, 189, 248, 0.03)',
            color: 'var(--text)',
            fontSize: '0.95rem',
            transition: 'all 0.3s ease',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--accent)';
            e.target.style.background = 'rgba(56, 189, 248, 0.08)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(56, 189, 248, 0.2)';
            e.target.style.background = 'rgba(56, 189, 248, 0.03)';
          }}
        />
      )}
    </div>
  );
}

// ActionIconButton Component
function ActionIconButton({ icon, onClick, color, tooltip }) {
  const colorMap = {
    accent: { bg: 'rgba(56, 189, 248, 0.12)', color: 'var(--accent)', hover: 'rgba(56, 189, 248, 0.2)' },
    warning: { bg: 'rgba(251, 191, 36, 0.12)', color: '#fbbf24', hover: 'rgba(251, 191, 36, 0.2)' },
    danger: { bg: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', hover: 'rgba(239, 68, 68, 0.2)' }
  };
  const colors = colorMap[color];

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={onClick}
        style={{
          padding: '0.5rem',
          borderRadius: '8px',
          border: `1.5px solid ${colors.color}20`,
          background: colors.bg,
          color: colors.color,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = colors.hover;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = colors.bg;
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {icon}
      </button>
      <div style={{
        position: 'absolute',
        bottom: '120%',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#333',
        color: 'white',
        padding: '0.5rem 0.75rem',
        borderRadius: '6px',
        fontSize: '0.8rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        opacity: 0,
        pointerEvents: 'none',
        transition: 'opacity 0.2s ease',
        zIndex: 999
      }}
      onMouseEnter={(e) => { e.parentElement.children[0].style.opacity = '0'; }}
      >
        {tooltip}
      </div>
    </div>
  );
}

export default AdminUsers;

